import { useState, useEffect, useMemo, useCallback } from 'react';
import { db } from '../firebase-config.js';
import { doc, getDoc, setDoc } from 'firebase/firestore';

export const BADGES = [
  {
    id: 'streak_0',
    name: 'Nouveau',
    days: 0,
    emoji: '🌱',
    color: '#FDF0E0',
    description: '0 à 6 jours consécutifs',
    theme: {
      bg1: '#FDF0E0',
      bg2: '#FBE7CF',
      border: '#EAD5BE',
      text: '#5A4635',
      shadow: 'rgba(90, 70, 53, 0.10)',
      shine: 'rgba(255, 255, 255, 0.40)',
      hoverShadow: 'rgba(90, 70, 53, 0.16)',
    }
  },
  {
    id: 'streak_7',
    name: 'Débutant Assidu',
    days: 7,
    emoji: '🔥',
    color: '#CBE2DB',
    description: '7 jours consécutifs de pratique',
    reward: 'Le guide « Créer son refuge au quotidien » vous est offert.',
    theme: {
      bg1: '#EEF6F3',
      bg2: '#CBE2DB',
      bg3: '#F7FBFA',
      border: '#B9D6CE',
      text: '#2F4A44',
      shadow: 'rgba(47, 74, 68, 0.10)',
      shine: 'rgba(255, 255, 255, 0.65)',
      hoverShadow: 'rgba(47, 74, 68, 0.16)',
    }
  },
  {
    id: 'streak_21',
    name: 'Habitué',
    days: 21,
    emoji: '⚡',
    color: '#C2C0E8',
    description: '21 jours consécutifs - Une habitude se forme !',
    reward: 'La respiration Oxygénation de l’organisme rejoint votre catalogue.',
    theme: {
      bg1: '#F3F2FC',
      bg2: '#C2C0E8',
      bg3: '#FBFAFE',
      border: '#B3AFDF',
      text: '#312E52',
      shadow: 'rgba(49, 46, 82, 0.08)',
      shine: 'rgba(255, 255, 255, 0.70)',
      hoverShadow: 'rgba(49, 46, 82, 0.14)',
    }
  },
  {
    id: 'streak_60',
    name: 'Maître Zen',
    days: 60,
    emoji: '🏆',
    color: '#D8B46E',
    description: '60 jours consécutifs — une régularité rare.',
    reward: 'Deux paysages sonores s’ouvrent : Grotte et Forêt nocturne.',
    theme: {
      bg1: '#D8B46E',
      bg2: '#E7C888',
      bg3: '#F5E6B3',
      border: '#D8B46E',
      text: '#4A3413',
      shadow: 'rgba(216, 180, 110, 0.28)',
      shine: 'rgba(255, 255, 255, 0.38)',
      hoverShadow: 'rgba(216, 180, 110, 0.40)',
    }
  }
];

const EMPTY_STREAK = Object.freeze({
  currentStreak: 0,
  longestStreak: 0,
  lastActiveDate: null,
  consecutiveDays: [],
});

// ── Local storage ────────────────────────────────────────────────────────────

function safeGet(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function safeSet(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch {
    console.warn('localStorage inaccessible');
  }
}

// ── Dates ────────────────────────────────────────────────────────────────────

function toISOParis(date = new Date()) {
  const f = new Intl.DateTimeFormat('fr-FR', {
    timeZone: 'Europe/Paris',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const [dd, mm, yyyy] = f.format(date).split('/');
  return `${yyyy}-${mm}-${dd}`;
}

function getYesterday(dateISO) {
  const [y, m, d] = dateISO.split('-').map(Number);
  const date = new Date(y, m - 1, d);
  date.setDate(date.getDate() - 1);
  return toISOParis(date);
}

// ── Helpers streak ───────────────────────────────────────────────────────────

function normalizeStreak(raw) {
  return {
    currentStreak: Number(raw?.currentStreak) || 0,
    longestStreak: Number(raw?.longestStreak) || 0,
    lastActiveDate: raw?.lastActiveDate || null,
    consecutiveDays: Array.isArray(raw?.consecutiveDays)
      ? [...new Set(raw.consecutiveDays)].sort().slice(-365)
      : [],
  };
}

function compareISODate(a, b) {
  if (!a && !b) return 0;
  if (!a) return -1;
  if (!b) return 1;
  return a.localeCompare(b);
}

function mergeStreakData(localData, remoteData) {
  const local = normalizeStreak(localData);
  const remote = normalizeStreak(remoteData);

  const mergedDays = [...new Set([...(local.consecutiveDays || []), ...(remote.consecutiveDays || [])])]
    .sort()
    .slice(-365);

  const dateCmp = compareISODate(local.lastActiveDate, remote.lastActiveDate);

  let preferred = local;
  if (dateCmp < 0) preferred = remote;
  else if (dateCmp === 0) {
    if (remote.currentStreak > local.currentStreak) preferred = remote;
    else if (remote.currentStreak === local.currentStreak && remote.longestStreak > local.longestStreak) {
      preferred = remote;
    }
  }

  return normalizeStreak({
    ...preferred,
    longestStreak: Math.max(local.longestStreak, remote.longestStreak),
    consecutiveDays: mergedDays,
  });
}

function ensureStreakNotBrokenData(data, today = toISOParis()) {
  const normalized = normalizeStreak(data);

  if (!normalized.lastActiveDate) return normalized;

  const yesterday = getYesterday(today);

  if (
    normalized.lastActiveDate === today ||
    normalized.lastActiveDate === yesterday
  ) {
    return normalized;
  }

  return {
    ...normalized,
    currentStreak: 0,
  };
}

function getUnlockedBadges(days) {
  return BADGES.filter((badge) => days >= badge.days);
}

function getNewlyUnlockedCelebrationBadge(previousDays, nextDays) {
  return BADGES.find(
    (badge) =>
      [7, 21, 60].includes(badge.days) &&
      previousDays < badge.days &&
      nextDays >= badge.days
  ) || null;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useStreak(userId) {
  const [lastBadge, setLastBadge] = useState(null);

  const streakKey = useMemo(
    () => (userId ? `streak:${userId}` : null),
    [userId]
  );

  const [streakData, setStreakData] = useState(EMPTY_STREAK);

  const readLocalStreak = useCallback(() => {
    if (!userId || !streakKey) return EMPTY_STREAK;

    try {
      const saved = safeGet(streakKey);
      return normalizeStreak(saved ? JSON.parse(saved) : EMPTY_STREAK);
    } catch (e) {
      console.error('Erreur lecture streak:', e);
      return EMPTY_STREAK;
    }
  }, [userId, streakKey]);

  const persistLocalStreak = useCallback((data) => {
    if (!streakKey) return;
    safeSet(streakKey, JSON.stringify(normalizeStreak(data)));
  }, [streakKey]);

  const persistRemoteStreak = useCallback(async (data) => {
    if (!userId) return;

    try {
      await setDoc(
        doc(db, 'users', userId),
        { streak: normalizeStreak(data) },
        { merge: true }
      );
    } catch (e) {
      console.warn('Erreur sauvegarde Firestore streak:', e);
    }
  }, [userId]);

  // Chargement initial : localStorage d'abord, puis Firestore
  useEffect(() => {
    let cancelled = false;

    if (!userId || !streakKey) {
      setStreakData(EMPTY_STREAK);
      return;
    }

    const localData = ensureStreakNotBrokenData(readLocalStreak());
    setStreakData(localData);
    persistLocalStreak(localData);

    (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', userId));
        if (cancelled) return;

        const remoteData = normalizeStreak(snap.data()?.streak);
        const merged = ensureStreakNotBrokenData(
          mergeStreakData(localData, remoteData)
        );

        setStreakData(merged);
        persistLocalStreak(merged);

        const mergedJson = JSON.stringify(merged);
        const remoteJson = JSON.stringify(remoteData);
        if (mergedJson !== remoteJson) {
          await persistRemoteStreak(merged);
        }
      } catch (e) {
        console.warn('Erreur synchronisation streak:', e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [userId, streakKey, readLocalStreak, persistLocalStreak, persistRemoteStreak]);

  // Vérifie régulièrement si la streak doit retomber à 0 après un jour manqué
  useEffect(() => {
    if (!userId || !streakKey) return;

    const syncFromLocal = () => {
      const next = ensureStreakNotBrokenData(readLocalStreak());
      setStreakData(next);
      persistLocalStreak(next);
    };

    syncFromLocal();

    window.addEventListener('storage', syncFromLocal);
    window.addEventListener('focus', syncFromLocal);

    const interval = setInterval(syncFromLocal, 30000);

    return () => {
      window.removeEventListener('storage', syncFromLocal);
      window.removeEventListener('focus', syncFromLocal);
      clearInterval(interval);
    };
  }, [userId, streakKey, readLocalStreak, persistLocalStreak]);

  // ✅ Appelé dès qu'une session est lancée
const markTodayActive = useCallback(async () => {
  if (!userId || !streakKey) return null;

  try {
    const today = toISOParis();
    const base = ensureStreakNotBrokenData(readLocalStreak(), today);

    // Déjà compté aujourd'hui
    if (base.lastActiveDate === today) return null;

    const yesterday = getYesterday(today);
    const continues =
      base.lastActiveDate === yesterday && base.currentStreak > 0;

    const newStreak = continues ? base.currentStreak + 1 : 1;

    const next = normalizeStreak({
      ...base,
      currentStreak: newStreak,
      longestStreak: Math.max(newStreak, base.longestStreak || 0),
      lastActiveDate: today,
      consecutiveDays: [...new Set([...(base.consecutiveDays || []), today])]
        .sort()
        .slice(-365),
    });

    const newlyUnlockedBadge = getNewlyUnlockedCelebrationBadge(
      Math.max(base.currentStreak, base.longestStreak || 0),
      next.currentStreak
    );

    setStreakData(next);
    persistLocalStreak(next);

    // On déclenche la célébration dès que l'écoute valide a été comptée localement
    if (newlyUnlockedBadge) {
      setLastBadge({ ...newlyUnlockedBadge });
    }

    await persistRemoteStreak(next);

    return newlyUnlockedBadge;
  } catch (error) {
    console.error('Erreur mise à jour streak:', error);
    return null;
  }
}, [userId, streakKey, readLocalStreak, persistLocalStreak, persistRemoteStreak]);

  const currentStreak = streakData.currentStreak;
  const longestStreak = streakData.longestStreak;
  const activeDaysCount = streakData.consecutiveDays.length;

  const unlockedBadges = useMemo(() => {
    return getUnlockedBadges(Math.max(currentStreak, longestStreak));
  }, [currentStreak, longestStreak]);

  const resetLastBadge = useCallback(() => {
    setLastBadge(null);
  }, []);

  return {
    currentStreak,
    longestStreak,
    activeDaysCount,
    unlockedBadges,
    lastBadge,
    resetLastBadge,
    markTodayActive,
  };
}